import { ReportingLanguage } from '../../../../i18n'
import { ReportingAccessLevels } from '../../../NonReact/constants/Permissions'
import { TelSysTypes } from '../../../NonReact/constants/TelephonyServerTypes'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { IsStringEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { OptionGroup, StandardOptionGroup } from '../../Common/Components/Selects/GroupedSelect/GroupedSelect'
import { SelectItem } from '../../Common/Components/Selects/Types'
import { SERVER_NONE_VALUE } from '../Constants'
import { LicenceResponseMapped } from '../../../NonReact/Types/Types'
import { Capabilities } from '../Types'
import { INTEGRATION_TYPE, IntegrationType } from '../../TelSysConfiguration/Constants/Constants'
import { resolveReportingLevelLabel } from './resolveReportingLevelLabel'

export interface ReportingLevelSelect extends SelectItem {
    otherLevels: ReportingAccessLevels[]
}

const LEGACY_ACCESS_LEVELS: string[] = [
    ReportingAccessLevels.NONE,
    ReportingAccessLevels.BLF,
    ReportingAccessLevels.ONE,
    ReportingAccessLevels.LITE,
    ReportingAccessLevels.ONE_THOUSAND,
    ReportingAccessLevels.TWO_THOUSAND
]

export const ENTERPRISE_ACCESS_LEVELS: string[] = [
    ReportingAccessLevels.ESSENTIALS,
    ReportingAccessLevels.PROFESSIONAL,
    ReportingAccessLevels.ENTERPRISE,
    ReportingAccessLevels.CALL_CENTRE_AGENT,
    ReportingAccessLevels.CALL_CENTRE_SUPERVISOR
]

const ALL_REPORTING_ACCESS_LEVELS_IN_ORDER: ReportingAccessLevels[] = [
    ReportingAccessLevels.NONE,
    ReportingAccessLevels.ESSENTIALS,
    ReportingAccessLevels.PROFESSIONAL,
    ReportingAccessLevels.ENTERPRISE,
    ReportingAccessLevels.CALL_CENTRE_AGENT,
    ReportingAccessLevels.CALL_CENTRE_SUPERVISOR,
    ReportingAccessLevels.BLF,
    ReportingAccessLevels.ONE,
    ReportingAccessLevels.LITE,
    ReportingAccessLevels.ONE_THOUSAND,
    ReportingAccessLevels.TWO_THOUSAND
]

/**
 * Returns the enterprise-tier reporting access levels applicable to the given telephony scope, in
 * display order. For MS Teams Business the licence-provisioning rules apply (Historic User in both
 * modes, Real-Time User / Agent in real-time only, Manager in both modes, never Enterprise); for
 * Legacy/other telsys the historic existing rules are preserved. Shared by the Add/Modify User modal
 * and the Fast Provisioning licence popup so they cannot drift.
 *
 * @param licenceSummary Available-licence counts, or null when unknown.
 * @param telSysType Telephony server type.
 * @param isTelSysHistoricOnly Whether the telsys is historic-only, or null when unknown.
 * @param integrationType MS Teams integration type (legacy/business), or null for non-Teams.
 * @param currentReportingLevel The user's currently-assigned reporting level (always kept selectable).
 * @returns Applicable enterprise reporting access levels in display order.
 */
export function applicableEnterpriseReportingLevels(
    licenceSummary: Nullable<LicenceResponseMapped>,
    telSysType: Nullable<TelSysTypes>,
    isTelSysHistoricOnly: Nullable<boolean>,
    integrationType: Nullable<IntegrationType>,
    currentReportingLevel: string
): ReportingAccessLevels[] {
    const isBusinessTeams: boolean =
        telSysType === TelSysTypes.MS_TEAMS && integrationType === INTEGRATION_TYPE.BUSINESS

    // Historic User (Essentials): Business offers it in both modes; Legacy/other keep the existing rule.
    const showEssentials: boolean = isBusinessTeams
        ? (licenceSummary?.AvailableLicences?.Essentials ?? 1) > 0 ||
          currentReportingLevel === ReportingAccessLevels.ESSENTIALS
        : !(
              ((licenceSummary?.AvailableLicences?.Essentials ?? 1) <= 0 ||
                  (isVarNotEmpty(isTelSysHistoricOnly) && !isTelSysHistoricOnly)) &&
              currentReportingLevel !== ReportingAccessLevels.ESSENTIALS
          )

    // Real-Time User (Professional): Business offers it only in real-time mode; Legacy/other keep the existing rule.
    const showProfessional: boolean = isBusinessTeams
        ? !isTelSysHistoricOnly &&
          ((licenceSummary?.AvailableLicences?.Professional ?? 1) > 0 ||
              currentReportingLevel === ReportingAccessLevels.PROFESSIONAL)
        : !(
              ((licenceSummary?.AvailableLicences?.Professional ?? 1) <= 0 ||
                  telSysType === TelSysTypes.MS_TEAMS ||
                  telSysType === TelSysTypes.WEBEX_CALLING) &&
              currentReportingLevel !== ReportingAccessLevels.PROFESSIONAL
          )

    // Enterprise: never offered for Business; Legacy/other keep the existing rule.
    const showEnterprise: boolean = isBusinessTeams
        ? false
        : !(
              ((licenceSummary?.AvailableLicences?.Enterprise ?? 1) <= 0 ||
                  (telSysType === TelSysTypes.MS_TEAMS && !isTelSysHistoricOnly) ||
                  telSysType === TelSysTypes.WEBEX_CALLING) &&
              currentReportingLevel !== ReportingAccessLevels.ENTERPRISE
          )

    // Agent (Call Centre Agent): Business offers it only in real-time mode; Legacy/other keep the existing rule.
    const showCallCentreAgent: boolean = isBusinessTeams
        ? !isTelSysHistoricOnly &&
          ((licenceSummary?.AvailableLicences?.CallCentreAgent ?? 1) > 0 ||
              currentReportingLevel === ReportingAccessLevels.CALL_CENTRE_AGENT)
        : !(
              ((licenceSummary?.AvailableLicences?.CallCentreAgent ?? 1) <= 0 || isTelSysHistoricOnly) &&
              currentReportingLevel !== ReportingAccessLevels.CALL_CENTRE_AGENT
          )

    // Manager (Call Centre Supervisor): Business offers it in both modes; Legacy/other keep the existing rule.
    const showCallCentreSupervisor: boolean = isBusinessTeams
        ? (licenceSummary?.AvailableLicences?.CallCentreSupervisor ?? 1) > 0 ||
          currentReportingLevel === ReportingAccessLevels.CALL_CENTRE_SUPERVISOR
        : !(
              ((licenceSummary?.AvailableLicences?.CallCentreSupervisor ?? 1) <= 0 || isTelSysHistoricOnly) &&
              currentReportingLevel !== ReportingAccessLevels.CALL_CENTRE_SUPERVISOR
          )

    const levels: ReportingAccessLevels[] = []

    if (showEssentials) levels.push(ReportingAccessLevels.ESSENTIALS)

    if (showProfessional) levels.push(ReportingAccessLevels.PROFESSIONAL)

    if (showEnterprise) levels.push(ReportingAccessLevels.ENTERPRISE)

    if (showCallCentreAgent) levels.push(ReportingAccessLevels.CALL_CENTRE_AGENT)

    if (showCallCentreSupervisor) levels.push(ReportingAccessLevels.CALL_CENTRE_SUPERVISOR)

    return levels
}

/** i18n label key for each enterprise reporting access level. */
const ENTERPRISE_LEVEL_LABEL_KEY: Readonly<Record<string, string>> = {
    [ReportingAccessLevels.ESSENTIALS]: ReportingLanguage.AccessLevelEssentials,
    [ReportingAccessLevels.PROFESSIONAL]: ReportingLanguage.AccessLevelProfessional,
    [ReportingAccessLevels.ENTERPRISE]: ReportingLanguage.AccessLevelEnterprise,
    [ReportingAccessLevels.CALL_CENTRE_AGENT]: ReportingLanguage.AccessLevelCallCentreAgent,
    [ReportingAccessLevels.CALL_CENTRE_SUPERVISOR]: ReportingLanguage.AccessLevelCallCentreSupervisor
}

/**
 * Resolves the display label for an enterprise reporting access level, applying the MS Teams
 * Business renaming (Historic User / Real-Time User / Agent / Manager) via {@link resolveReportingLevelLabel}.
 *
 * @param language Loaded language handler.
 * @param level Enterprise reporting access level.
 * @param telSysType Telephony server type.
 * @param integrationType MS Teams integration type, or null for non-Teams.
 * @returns The display label for the level.
 */
export function enterpriseReportingLevelLabel(
    language: CLanguageHandler,
    level: ReportingAccessLevels,
    telSysType: Nullable<TelSysTypes>,
    integrationType: Nullable<IntegrationType>
): string {
    return resolveReportingLevelLabel(language, ENTERPRISE_LEVEL_LABEL_KEY[level], telSysType, integrationType)
}

/**
 * Reporting Access Level - create a list of reporting access level options and filter by a given level.
 */
export default class ReportingAccessLevel {
    private language: CLanguageHandler

    constructor(language: CLanguageHandler) {
        this.language = language
    }

    /**
     * Compares current reporting level with max reporting level and returns lesser of two.
     * @param currentLevel user's reporting level
     * @param maxLevel maximum reporting level allowed by partition/telsys
     * @returns current level or max level whichever is lesser
     */
    public getTopLevelOption = (currentLevel: string, maxLevel: string): string => {
        const availableOptions: string[] =
            maxLevel === ReportingAccessLevels.ENTERPRISE
                ? ENTERPRISE_ACCESS_LEVELS
                : LEGACY_ACCESS_LEVELS.slice(
                      0,
                      LEGACY_ACCESS_LEVELS.findIndex((accessLevel: string): boolean => accessLevel === maxLevel)
                  )
        const isCurrentLevelAvailable: boolean = availableOptions.includes(currentLevel)

        return isCurrentLevelAvailable ? currentLevel : maxLevel
    }

    /**
     * Filter options by a given level.
     *
     * @param options - The options to filter.
     * @param level - The level to filter by.
     *
     * @returns - A list of filtered options.
     */
    private filterOptions = (options: ReportingLevelSelect[], level?: ReportingAccessLevels): ReportingLevelSelect[] =>
        !IsStringEmpty(level)
            ? options.filter((personalOption: ReportingLevelSelect): boolean =>
                  personalOption.otherLevels.includes(level)
              )
            : options

    /**
     * The no reporting options.
     *
     * @returns A list of no reporting options.
     */
    private noReporting = (): StandardOptionGroup => ({
        groupTitle: this.language.get(ReportingLanguage.ReportingLevelTypesNone),
        options: [
            {
                label: this.language.get(ReportingLanguage.AccessLevelNone),
                value: SERVER_NONE_VALUE
            }
        ]
    })

    /**
     * Personal reporting options.
     *
     * @param level - The level to filter by.
     *
     * @returns A of personal reporting options filtered by level.
     */
    private personalReporting = (
        level: ReportingAccessLevels = ReportingAccessLevels.TWO_THOUSAND
    ): Nullable<OptionGroup<ReportingLevelSelect>> => {
        const personalOptions: ReportingLevelSelect[] = [
            {
                label: this.language.get(ReportingLanguage.AccessLevelPresence),
                value: ReportingAccessLevels.BLF,
                otherLevels: [
                    ReportingAccessLevels.BLF,
                    ReportingAccessLevels.ONE,
                    ReportingAccessLevels.LITE,
                    ReportingAccessLevels.ONE_THOUSAND,
                    ReportingAccessLevels.TWO_THOUSAND
                ]
            },
            {
                label: this.language.get(ReportingLanguage.AccessLevelOne),
                value: ReportingAccessLevels.ONE,
                otherLevels: [
                    ReportingAccessLevels.ONE,
                    ReportingAccessLevels.LITE,
                    ReportingAccessLevels.ENTERPRISE,
                    ReportingAccessLevels.CALL_CENTRE_SUPERVISOR,
                    ReportingAccessLevels.ONE_THOUSAND,
                    ReportingAccessLevels.TWO_THOUSAND
                ]
            }
        ]

        const filteredPersonalOptions: ReportingLevelSelect[] = this.filterOptions(personalOptions, level)

        return filteredPersonalOptions.length > 0
            ? {
                  groupTitle: this.language.get(ReportingLanguage.ReportingLevelTypesPersonal),
                  options: filteredPersonalOptions
              }
            : undefined
    }

    /**
     * Enterprise reporting options.
     *
     * @param licenceSummary - The licence summary.
     * @param telSysType - The Tel servers type.
     * @param currentReportingLevel
     *
     * @returns - A list of Enterprise reporting options.
     */
    private enterpriseReporting = (
        licenceSummary: Nullable<LicenceResponseMapped>,
        telSysType: Nullable<TelSysTypes>,
        isTelSysHistoricOnly: Nullable<boolean>,
        integrationType: Nullable<IntegrationType>,
        currentReportingLevel: string
    ): Nullable<OptionGroup<ReportingLevelSelect>> => {
        const enterpriseOtherLevels: ReportingAccessLevels[] = [
            ReportingAccessLevels.ESSENTIALS,
            ReportingAccessLevels.PROFESSIONAL,
            ReportingAccessLevels.ENTERPRISE,
            ReportingAccessLevels.CALL_CENTRE_AGENT,
            ReportingAccessLevels.CALL_CENTRE_SUPERVISOR
        ]

        const options: ReportingLevelSelect[] = applicableEnterpriseReportingLevels(
            licenceSummary,
            telSysType,
            isTelSysHistoricOnly,
            integrationType,
            currentReportingLevel
        ).map(
            (level: ReportingAccessLevels): ReportingLevelSelect => ({
                label: enterpriseReportingLevelLabel(this.language, level, telSysType, integrationType),
                value: level,
                otherLevels: enterpriseOtherLevels
            })
        )

        return {
            groupTitle: this.language.get(ReportingLanguage.ReportingLevelTypesEnterprise),
            options
        }
    }

    /**
     * Supervisor reporting options.
     *
     * @param level - The level to filter by.
     *
     * @returns - A list of supervisor reporting options.
     */
    private supervisorReporting = (
        level: ReportingAccessLevels = ReportingAccessLevels.TWO_THOUSAND
    ): Nullable<OptionGroup<ReportingLevelSelect>> => {
        const supervisorOptions: ReportingLevelSelect[] = [
            {
                label: this.language.get(ReportingLanguage.AccessLevelLite),
                value: ReportingAccessLevels.LITE,
                otherLevels: [
                    ReportingAccessLevels.LITE,
                    ReportingAccessLevels.ONE_THOUSAND,
                    ReportingAccessLevels.TWO_THOUSAND
                ]
            },
            {
                label: this.language.get(ReportingLanguage.AccessLevel1000),
                value: ReportingAccessLevels.ONE_THOUSAND,
                otherLevels: [ReportingAccessLevels.ONE_THOUSAND, ReportingAccessLevels.TWO_THOUSAND]
            },
            {
                label: this.language.get(ReportingLanguage.AccessLevel2000),
                value: ReportingAccessLevels.TWO_THOUSAND,
                otherLevels: [ReportingAccessLevels.TWO_THOUSAND]
            }
        ]

        const filteredSupervisorOptions: ReportingLevelSelect[] = this.filterOptions(supervisorOptions, level)

        return filteredSupervisorOptions.length > 0
            ? {
                  groupTitle: this.language.get(ReportingLanguage.ReportingLevelTypesSupervisor),
                  options: filteredSupervisorOptions
              }
            : undefined
    }

    public getAvailableReportingLevelsAsStrings = (reportingLevels: Nullable<StandardOptionGroup>[]): string[] =>
        reportingLevels
            .map(
                (reportingLevel: Nullable<StandardOptionGroup>): string[] =>
                    reportingLevel?.options?.map((option: SelectItem): string => option.value) ?? []
            )
            .flat()

    /**
     * Get the highest access level from a list of access levels.
     *
     * @param reportingLevels - list of access levels.
     * @returns The highest access level.
     */
    public getHighestReportingLevelFromList = (reportingLevels: Nullable<StandardOptionGroup>[]): string =>
        this.getHighestReportingLevelFromListBase(this.getAvailableReportingLevelsAsStrings(reportingLevels))

    /**
     * Get the highest access level from a list of access levels.
     *
     * @param reportingLevels - list of access levels.
     * @returns The highest access level.
     */
    public getHighestReportingLevelFromListBase = (reportingLevels: string[]): string =>
        ALL_REPORTING_ACCESS_LEVELS_IN_ORDER[
            reportingLevels.reduce((highestLevel: number, level: string): number => {
                const levelValue: number = ALL_REPORTING_ACCESS_LEVELS_IN_ORDER.findIndex(
                    (accessLevel: string): boolean => accessLevel === level
                )

                return levelValue > highestLevel ? levelValue : highestLevel
            }, 0)
        ]

    /**
     * Get a reporting levels list filtered by level.
     *
     * @param capabilities - The capabilities.
     * @param telSysType - Current Tel servers type.
     * @param licenceSummary - The licence summary.
     * @param currentReportingLevel - The current reporting level.
     * @param levels - The levels to filter by.
     *
     * @returns - Get the options group list.
     */
    public getReportingLevels = (
        capabilities: Capabilities,
        licenceSummary: Nullable<LicenceResponseMapped>,
        telSysType: Nullable<TelSysTypes>,
        isTelSysHistoricOnly: Nullable<boolean>,
        integrationType: Nullable<IntegrationType>,
        currentReportingLevel: string,
        levels: ReportingAccessLevels[] = [ReportingAccessLevels.TWO_THOUSAND, ReportingAccessLevels.ENTERPRISE]
    ): Nullable<StandardOptionGroup>[] => {
        const highestLevel: ReportingAccessLevels = this.getHighestReportingLevelFromListBase(
            levels
        ) as ReportingAccessLevels
        const hasEnterprise: boolean = !!levels?.includes(ReportingAccessLevels.ENTERPRISE) && capabilities.enterprise
        const hasLegacy: boolean =
            !!levels?.some((level: string): boolean => LEGACY_ACCESS_LEVELS.includes(level)) && capabilities.legacy

        return [
            this.noReporting(),
            ...(hasLegacy ? [this.personalReporting(highestLevel)] : []),
            ...(hasEnterprise
                ? [
                      this.enterpriseReporting(
                          licenceSummary,
                          telSysType,
                          isTelSysHistoricOnly,
                          integrationType,
                          currentReportingLevel
                      )
                  ]
                : []),
            ...(hasLegacy ? [this.supervisorReporting(highestLevel)] : [])
        ]
    }

    public static newInstance(language: CLanguageHandler): ReportingAccessLevel {
        return new ReportingAccessLevel(language)
    }
}

export { ReportingAccessLevels } from '../../../NonReact/constants/Permissions'
